/**
 * Which tab triggers stay in the list and which move into "More (n)".
 *
 * Triggers keep their order. The active trigger always stays visible. When
 * every trigger fits, nothing moves and no "More" button is needed;
 * otherwise the "More" button's width (and its gap) is reserved first, the
 * active trigger is kept, and the others are taken in order while they fit.
 */
export interface TabsPartitionInput {
  /** Each trigger's width, in list order. */
  widths: readonly number[];
  /** The list's inner width. */
  available: number;
  /** The gap between neighbouring items. */
  gap: number;
  /** The "More" button's width. */
  moreWidth: number;
  /** The active trigger's index, or -1 when none is active. */
  activeIndex: number;
}

export interface TabsPartition {
  visible: number[];
  overflow: number[];
}

export function partitionTabs({
  widths,
  available,
  gap,
  moreWidth,
  activeIndex,
}: TabsPartitionInput): TabsPartition {
  const all = widths.map((_, index) => index);
  const total =
    widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1);
  if (total <= available) return { visible: all, overflow: [] };

  let used = moreWidth;
  const keep = new Set<number>();
  if (activeIndex >= 0 && activeIndex < widths.length) {
    keep.add(activeIndex);
    used += gap + (widths[activeIndex] ?? 0);
  }
  for (const index of all) {
    if (keep.has(index)) continue;
    const next = used + gap + (widths[index] ?? 0);
    if (next > available) break;
    keep.add(index);
    used = next;
  }
  return {
    visible: all.filter((index) => keep.has(index)),
    overflow: all.filter((index) => !keep.has(index)),
  };
}
